/**
 * Lift2Fit — admin: Lifters (`#/admin/lifters`).
 *
 * The lifter side of the platform: every account that is not a business, with
 * its tier, coaching plan, whether it is premium right now, whether it has a
 * coach link, and when it was last active. Search and the premium/coach-link
 * filters run server-side, so the count is the platform's answer, not a
 * client-side slice of one page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type AdminAccountView } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { AlertIcon, ArrowRightIcon, TargetIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { Link } from '../router';
import { ADMIN_EYEBROW, coachPlanLabel, formatCount, tierLabel } from './shared';

type Tri = 'any' | 'yes' | 'no';

function triValue(value: Tri): boolean | null {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
}

export function AdminLiftersScreen() {
  const [search, setSearch] = useState('');
  const [premium, setPremium] = useState<Tri>('any');
  const [linked, setLinked] = useState<Tri>('any');
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState<AdminAccountView[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreBusy, setMoreBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const loadFirstPage = useCallback(async () => {
    const request = requestRef.current + 1;
    requestRef.current = request;
    setLoading(true);
    setError(null);
    const result = await api.adminAccounts({
      role: 'lifter',
      q: search.trim() === '' ? null : search.trim(),
      premium: triValue(premium),
      linked: triValue(linked),
      limit: pageSize,
    });
    if (requestRef.current !== request) return;
    if (result.problem || !result.data) {
      setRows([]);
      setTotal(0);
      setNextCursor(null);
      setError(result.problem?.detail ?? result.problem?.title ?? 'The lifters list did not load.');
      setLoading(false);
      return;
    }
    setRows(result.data.data);
    setTotal(result.data.total);
    setNextCursor(result.data.nextCursor);
    setLoading(false);
  }, [search, premium, linked, pageSize]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = async () => {
    if (nextCursor === null) return;
    setMoreBusy(true);
    const result = await api.adminAccounts({
      role: 'lifter',
      q: search.trim() === '' ? null : search.trim(),
      premium: triValue(premium),
      linked: triValue(linked),
      limit: pageSize,
      cursor: nextCursor,
    });
    setMoreBusy(false);
    if (result.problem || !result.data) {
      setError(result.problem?.detail ?? result.problem?.title ?? 'The next page did not load.');
      return;
    }
    const page = result.data;
    setRows((current) => [...current, ...page.data]);
    setNextCursor(page.nextCursor);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="admin-lifters">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Lifters"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="Every account on the lifter side: tier, plan, premium state, whether a coach is linked, and last activity. No training content — counts and dates only."
        actions={
          <AppLinkButton to="/admin/businesses" variant="outline">
            Businesses
          </AppLinkButton>
        }
      />

      <Panel
        title="Lifter accounts"
        subtitle="Server-side search and filters; the count is the whole matching set."
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Search</span>
              <input
                type="search"
                data-testid="admin-lifters-search"
                value={search}
                placeholder="alex@lift2fit.com"
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 w-52 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Premium</span>
              <select
                data-testid="admin-lifters-premium"
                value={premium}
                onChange={(event) => setPremium(event.target.value as Tri)}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                <option value="any">Any</option>
                <option value="yes">Premium</option>
                <option value="no">Free</option>
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Coach link</span>
              <select
                data-testid="admin-lifters-linked"
                value={linked}
                onChange={(event) => setLinked(event.target.value as Tri)}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                <option value="any">Any</option>
                <option value="yes">Linked</option>
                <option value="no">Unlinked</option>
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Rows</span>
              <select
                data-testid="admin-lifters-limit"
                value={String(pageSize)}
                onChange={(event) => setPageSize(Number(event.target.value))}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                {[5, 10, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
      >
        <p className="text-[12px] text-ink-muted" data-testid="admin-lifters-total">
          {loading ? 'Searching…' : `${formatCount(total)} lifter(s) match`}
        </p>

        {error ? (
          <p
            role="alert"
            data-testid="admin-lifters-error"
            className="border-l-2 border-danger bg-page-subtle px-3 py-2 text-[13px] text-danger"
          >
            {error}
          </p>
        ) : null}

        {loading && rows.length === 0 ? (
          <LoadingPanel label="Loading lifters" rows={5} />
        ) : rows.length === 0 && !error ? (
          <EmptyState
            title="No lifter matches those filters"
            body="Try a different email or name, or clear the premium and coach-link filters. Every account is listed here."
            icon={<TargetIcon className="h-8 w-8 text-accent" />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] border-collapse text-left">
              <thead>
                <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  <th className="py-2 pr-3 font-bold">Lifter</th>
                  <th className="py-2 pr-3 font-bold">Tier</th>
                  <th className="py-2 pr-3 font-bold">Plan</th>
                  <th className="py-2 pr-3 font-bold">Premium</th>
                  <th className="py-2 pr-3 font-bold">Coach</th>
                  <th className="py-2 pr-3 font-bold">Last activity</th>
                  <th className="py-2 font-bold">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((account) => (
                  <tr
                    key={account.userId}
                    data-testid={`admin-lifter-row-${account.userId}`}
                    className="border-b border-hairline last:border-b-0 align-top"
                  >
                    <td className="py-3 pr-3">
                      <span className="flex min-w-0 flex-col">
                        <span className="text-[13px] font-bold text-ink">{account.displayName}</span>
                        <span className="text-[12px] text-ink-muted">{account.email}</span>
                        <span className="text-[11px] text-ink-muted">joined {formatDate(account.createdAt)}</span>
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink">{tierLabel(account.tier)}</td>
                    <td className="py-3 pr-3 text-[12px] text-ink">{coachPlanLabel(account.coachPlan)}</td>
                    <td className="py-3 pr-3" data-testid={`admin-lifter-premium-${account.userId}`}>
                      {account.isPremium ? (
                        <StatusChip tone="success">Premium</StatusChip>
                      ) : (
                        <StatusChip tone="neutral">Free</StatusChip>
                      )}
                    </td>
                    <td className="py-3 pr-3" data-testid={`admin-lifter-coach-${account.userId}`}>
                      {account.hasCoachLink ? (
                        <StatusChip tone="accent">Linked</StatusChip>
                      ) : (
                        <StatusChip tone="neutral">No coach</StatusChip>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">
                      {account.lastActivityAt ? formatDate(account.lastActivityAt) : 'never'}
                    </td>
                    <td className="py-3">
                      <Link
                        to={`/admin/accounts/${account.userId}`}
                        data-testid={`admin-lifter-open-${account.userId}`}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-accent hover:text-accent-hover"
                      >
                        Open
                        <ArrowRightIcon />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nextCursor !== null ? (
          <button
            type="button"
            data-testid="admin-lifters-more"
            disabled={moreBusy}
            onClick={() => void loadMore()}
            className="inline-flex h-9 w-fit items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
          >
            {moreBusy ? 'Loading…' : 'Load more'}
          </button>
        ) : null}
      </Panel>

      {!loading && rows.length > 0 ? (
        <p className="text-[12px] text-ink-muted">
          Showing {formatCount(rows.length)} of {formatCount(total)} matching lifter(s).{' '}
          <Link to="/admin" className="text-accent hover:text-accent-hover">
            Back to the overview
          </Link>
          <AlertIcon className="ml-1 inline text-[12px]" />
        </p>
      ) : null}
    </div>
  );
}
